import type { ExpansionId } from '@onuw/roles-data'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { PRESETS } from '../../ui/presets'
import { rolesOfExpansion } from '../roleFacts'
import { Deck, R, RoleList } from './parts'

/** The ready-made deck that shows a pack off, with the players it is made for. */
function Preset({ id, players, children }: { id: string; players: number; children: ReactNode }) {
  const { t } = useTranslation()
  const preset = PRESETS.find((p) => p.id === id)
  if (!preset) return null
  return (
    <>
      <h2>A deck to start with</h2>
      <p>
        {children} In the role picker it is the "{t(`ui:lobby.presets.${id}`)}" button, made for {players}{' '}
        players: <Deck roles={preset.roles} />. More in <Link to="/guides/decks/">decks for every table</Link>
        .
      </p>
    </>
  )
}

function Roles({ expansion, also }: { expansion: ExpansionId; also?: ExpansionId }) {
  const roles = [...rolesOfExpansion(expansion), ...(also ? rolesOfExpansion(also) : [])]
  return (
    <>
      <h2>The {roles.length} roles</h2>
      <RoleList roles={roles} />
    </>
  )
}

export function BaseGame() {
  return (
    <>
      <p>
        The base game is twelve roles and one idea: a single night, after which nobody is sure what they are.
        Two <R id="werewolf">Werewolves</R> hide among villagers who each know one small thing, and a few
        cards move while everyone sleeps. Everything the later packs add is a variation on this.
      </p>

      <h2>What it teaches</h2>
      <ul>
        <li>
          <strong>Information.</strong> The <R id="seer" /> looks at cards, the <R id="mason">Masons</R> know
          each other, the <R id="insomniac" /> sees how her night ended.
        </li>
        <li>
          <strong>Doubt.</strong> The <R id="robber" />, the <R id="troublemaker" /> and the <R id="drunk" />{' '}
          move cards, so yesterday’s fact may be about somebody else by morning.
        </li>
        <li>
          <strong>Motives.</strong> The <R id="minion" /> wants to die for the wolves, the <R id="tanner" />{' '}
          wants to die for himself, and the <R id="hunter" /> takes someone with him.
        </li>
        <li>
          <strong>Copies.</strong> The <R id="doppelganger" /> becomes whatever she looks at. Leave her out of
          the first few games.
        </li>
      </ul>
      <Preset id="classic" players={3}>
        Two Werewolves, a Seer, a Robber, a Troublemaker and a Villager: six cards, the smallest deck there
        is.
      </Preset>
      <Roles expansion="base" />
      <p>
        New to the game? Read <Link to="/rules/">how to play</Link>, then{' '}
        <Link to="/guides/strategy/">the strategy guide</Link>.
      </p>
    </>
  )
}

export function Daybreak() {
  return (
    <>
      <p>
        Daybreak is the first expansion, and the one to add first. Its eleven roles keep the base game’s shape
        and sharpen every part of it: wolves that do more than hide, villagers who protect and reveal, and
        tokens that change the table itself.
      </p>

      <h2>What it adds</h2>
      <ul>
        <li>
          <strong>Three more wolves.</strong> The <R id="alphaWolf" /> turns a villager into a wolf with a
          fourth center card, the <R id="mysticWolf" /> looks at a player’s card, and the <R id="dreamWolf" />{' '}
          sleeps through the pack’s meeting.
        </li>
        <li>
          <strong>The shield.</strong> The <R id="sentinel" /> makes one card untouchable for the night.
        </li>
        <li>
          <strong>Public facts.</strong> The <R id="revealer" /> turns a card face up for everyone.
        </li>
        <li>
          <strong>Roles that can change sides.</strong> The <R id="paranormalInvestigator" /> becomes the wolf
          he finds, and the <R id="witch" /> hands a center card to a player of her choice.
        </li>
        <li>
          <strong>Artifacts.</strong> The <R id="curator" /> gives a player a random token that may rewrite
          their role. See <Link to="/guides/marks-and-artifacts/">marks and artifacts</Link>.
        </li>
        <li>
          <strong>Protection in the vote.</strong> The <R id="bodyguard" /> saves whoever he votes for.
        </li>
      </ul>
      <Preset id="daybreak" players={4}>
        A Werewolf and the Alpha Wolf against a Seer, a Witch, a Robber, a Troublemaker, a Revealer and an
        Insomniac.
      </Preset>
      <Roles expansion="daybreak" />
    </>
  )
}

export function BonusRoles() {
  return (
    <>
      <p>
        The bonus roles are the single cards published alongside the main sets. Each is one sharp idea that
        drops into any deck: a player who cannot die, a villager who may turn, a second servant for the
        wolves. This table also has one role of its own, the <R id="giantWolf" />.
      </p>

      <h2>Where they fit</h2>
      <ul>
        <li>
          <strong>For the village:</strong> the <R id="auraSeer" /> learns who acted at night, the{' '}
          <R id="beholder" /> finds the Seers, the <R id="thing" /> taps a neighbour, the <R id="prince" />{' '}
          cannot be voted out, and the <R id="empath" /> watches everyone point.
        </li>
        <li>
          <strong>For the wolves:</strong> the <R id="squire" /> sees where the wolf cards ended up, and the
          Giant Wolf votes twice.
        </li>
        <li>
          <strong>Unsure of their side:</strong> the <R id="cursed" /> turns if a wolf votes for them, and{' '}
          <R id="nostradamus" /> joins the team of the last card he saw, in front of the whole table.
        </li>
        <li>
          <strong>For themselves:</strong> the <R id="appTanner" /> wants the Tanner dead.
        </li>
        <li>
          <strong>For the Aliens:</strong> the <R id="bodySnatcher" /> turns the card he takes into an Alien
          card. He belongs in a deck with the <Link to="/guides/alien/">Alien pack</Link>.
        </li>
      </ul>
      <Roles expansion="bonus" also="custom" />
    </>
  )
}

export function Vampire() {
  return (
    <>
      <p>
        The Vampire pack adds a second evil team and a second layer to the table: <strong>marks</strong>.
        Before the night comes dusk, when marks are handed out, and a mark can change your team, link your
        death to someone else’s, or stop you acting at all.
      </p>

      <h2>How it changes the game</h2>
      <ul>
        <li>
          <strong>Dusk.</strong> With any marking role in the deck, everyone starts with a Mark of Clarity.
          The dusk roles replace marks, then everyone looks at their own. The night follows as usual.
        </li>
        <li>
          <strong>Vampires recruit.</strong> The <R id="vampire" />, the <R id="count">Count</R> and the{' '}
          <R id="master">Master</R> give one player the Mark of the Vampire, and that player is a Vampire from
          then on. <R id="renfield" /> serves them as the Minion serves the wolves.
        </li>
        <li>
          <strong>Marks with consequences.</strong> <R id="cupid" /> links two deaths, the <R id="diseased" />{' '}
          makes a player costly to vote for, the <R id="instigator" /> turns a player against their own team,
          and the <R id="priest" /> wipes a mark clean.
        </li>
        <li>
          <strong>A contract.</strong> The <R id="assassin" /> wins if his marked player dies, and the{' '}
          <R id="appAssassin" /> wins if the Assassin does.
        </li>
        <li>
          <strong>Marks move at night.</strong> The <R id="marksman" /> reads one, the <R id="pickpocket" />{' '}
          steals one, and the <R id="gremlin" /> swaps two.
        </li>
        <li>
          <strong>A copy from the center.</strong> The <R id="copycat" /> becomes a card nobody was dealt.
        </li>
      </ul>
      <p>
        Every mark and its rulings are in <Link to="/guides/marks-and-artifacts/">marks and artifacts</Link>.
        With Vampires and Werewolves in one deck, the village must kill one of each in the same vote.
      </p>
      <Preset id="vampires" players={6}>
        The three Vampires and Renfield against a Priest, a Marksman, a Seer, a Robber and a Villager. For the
        marks without the Vampires, the "Marks" deck pairs two Werewolves with seven marking roles.
      </Preset>
      <Roles expansion="vampire" />
    </>
  )
}

export function Alien() {
  return (
    <>
      <p>
        The Alien pack hands part of the game to the app. Its roles do not have fixed powers: each game the
        app draws an instruction for them and announces it to the whole table. Everyone knows what kind of
        thing the Aliens did tonight. Nobody knows who they are.
      </p>

      <h2>How it changes the game</h2>
      <ul>
        <li>
          <strong>A third evil team.</strong> The <R id="alien">Aliens</R> wake together and carry out the
          announced action: look at cards, pass their cards around, show each other what they hold, or
          nothing.
        </li>
        <li>
          <strong>Aliens against each other.</strong> <R id="groob" /> and <R id="zerb" /> each win only if
          the other dies. The <R id="syntheticAlien" /> wakes with the Aliens and wins by dying, which loses
          the game for them.
        </li>
        <li>
          <strong>Announced instructions.</strong> The <R id="psychic" />, the <R id="rascal" />, the{' '}
          <R id="exposer" />, the <R id="mortician" /> and the <R id="oracle" /> all act on what the app calls
          out. The table hears the instruction even when the card lies in the center.
        </li>
        <li>
          <strong>Player numbers.</strong> Every player gets a number, and instructions use it: an even
          player, a player with a higher number.
        </li>
        <li>
          <strong>Villagers who sense the enemy.</strong> The <R id="cow" /> feels an Alien next to it, and
          the <R id="leader" /> knows them all, at the risk of being voted out by them.
        </li>
        <li>
          <strong>Neighbours matter.</strong> The <R id="blob" /> wins if the players around it survive. The
          Mortician wins if one beside him dies. Arrange the seats before you start.
        </li>
      </ul>
      <p>
        In a deck without alien cards, the Cow and the Leader sense the Werewolves instead, so both work in
        any game.
      </p>
      <Preset id="aliens" players={6}>
        An Alien, Groob, Zerb, the Synthetic Alien and the Body Snatcher against a Leader, a Rascal, an Empath
        and a Seer.
      </Preset>
      <Roles expansion="alien" />
    </>
  )
}
